/**
 * Configurações > Minha empresa: dados cadastrais, logo, locais e a situação
 * das configurações da empresa.
 *
 * Os dados cadastrais são a fonte única: a nota fiscal, os PDFs e os e-mails
 * leem CNPJ, razão social e endereço daqui. Todo mundo vê; só quem tem
 * `empresa:configurar` altera (o backend decide e devolve `pode_editar`).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import AsyncButton from "../../../components/AsyncButton";
import { useAuth } from "../../../context/AuthContext";
import {
  TIPOS_LOCAL,
  criarLocal,
  lerMinhaEmpresa,
  listarLocais,
  salvarLocal,
  salvarMinhaEmpresa,
} from "../../../services/empresa";
import { PESSOA_JURIDICA, erroDoDocumento, mascaraDocumento } from "../../../utils/documento";
import LogoDaEmpresa from "./LogoDaEmpresa";
import "./minha-empresa.css";

const CAMPOS = [
  { campo: "nome", rotulo: "Nome da empresa", dica: "Como ela aparece no sistema" },
  { campo: "razao_social", rotulo: "Razão social" },
  { campo: "cnpj", rotulo: "CNPJ" },
  { campo: "inscricao_estadual", rotulo: "Inscrição estadual" },
  { campo: "telefone", rotulo: "Telefone", tipo: "tel" },
  { campo: "email", rotulo: "E-mail", tipo: "email" },
  { campo: "site", rotulo: "Site", col2: true },
];
const CAMPOS_ENDERECO = [
  { campo: "cep", rotulo: "CEP" },
  { campo: "endereco", rotulo: "Endereço (rua)", col2: true },
  { campo: "numero", rotulo: "Número" },
  { campo: "complemento", rotulo: "Complemento" },
  { campo: "bairro", rotulo: "Bairro" },
  { campo: "cidade", rotulo: "Cidade" },
  { campo: "estado", rotulo: "UF", maxLength: 2 },
];
const LOCAL_VAZIO = {
  nome: "", tipo: "FABRICA", cep: "", endereco: "", numero: "", complemento: "", bairro: "",
  cidade: "", estado: "", saida_das_entregas: false, ativo: true,
};

function formatarCep(valor) {
  const d = String(valor || "").replace(/\D/g, "");
  return d.length === 8 ? `${d.slice(0, 5)}-${d.slice(5)}` : valor || "";
}

// Normalização só no onBlur (ADR-0059): no onChange ela reescreveria o que a
// pessoa está digitando.
function normalizarAoSair(campo, valor) {
  if (campo === "cnpj") return mascaraDocumento(valor, PESSOA_JURIDICA);
  if (campo === "cep") return formatarCep(valor);
  if (campo === "estado") return String(valor || "").trim().toUpperCase();
  return valor;
}

function Campo({ def, valor, onChange, onBlur, disabled, erro, prefixo = "empresa" }) {
  const id = `${prefixo}-${def.campo}`;
  return (
    <div className={`input-group${def.col2 ? " col-2" : ""}`}>
      <label className="input-label" htmlFor={id}>{def.rotulo}</label>
      <input
        id={id}
        className="input"
        type={def.tipo || "text"}
        value={valor ?? ""}
        maxLength={def.maxLength}
        disabled={disabled}
        onChange={(e) => onChange(def.campo, e.target.value)}
        onBlur={(e) => onBlur(def.campo, e.target.value)}
        aria-invalid={Boolean(erro)}
      />
      {erro ? <span className="me-erro">{erro}</span> : def.dica && <span className="me-dica">{def.dica}</span>}
    </div>
  );
}

function DadosDaEmpresa({ dados, podeEditar, onSalvo }) {
  const { request } = useAuth();
  const inicial = useMemo(() => ({
    ...dados,
    cnpj: mascaraDocumento(dados.cnpj, PESSOA_JURIDICA),
    cep: formatarCep(dados.cep),
  }), [dados]);
  const [form, setForm] = useState(inicial);
  const [erro, setErro] = useState("");
  useEffect(() => setForm(inicial), [inicial]);

  const mudou = Object.keys(inicial).some((c) => (form[c] ?? "") !== (inicial[c] ?? ""));
  const erroCnpj = erroDoDocumento(form.cnpj, PESSOA_JURIDICA);
  const alterar = (campo, valor) => setForm((f) => ({ ...f, [campo]: valor }));
  const sair = (campo, valor) => setForm((f) => ({ ...f, [campo]: normalizarAoSair(campo, valor) }));

  async function salvar() {
    setErro("");
    try {
      onSalvo(await salvarMinhaEmpresa(request, form));
    } catch (e) {
      setErro(e?.message || "Não foi possível salvar.");
      throw e;
    }
  }

  return (
    <section className="set-section">
      <div className="set-section__head">
        <h2 className="set-section__title">Dados da empresa</h2>
        <p className="set-section__desc">
          Saem na nota fiscal, nos orçamentos e nos e-mails para o cliente.
        </p>
      </div>
      <div className="set-section__body">
        <div className="set-fields">
          {CAMPOS.map((def) => (
            <Campo
              key={def.campo} def={def} valor={form[def.campo]} onChange={alterar} onBlur={sair}
              disabled={!podeEditar} erro={def.campo === "cnpj" ? erroCnpj : ""}
            />
          ))}
        </div>
        <h3 className="me-subtitulo">Endereço</h3>
        <div className="set-fields me-endereco">
          {CAMPOS_ENDERECO.map((def) => (
            <Campo key={def.campo} def={def} valor={form[def.campo]} onChange={alterar} onBlur={sair} disabled={!podeEditar} />
          ))}
        </div>
        {erro && <p className="me-erro" role="alert">{erro}</p>}
        {!podeEditar && (
          <p className="me-dica">Só quem tem a permissão “Configurar a empresa” pode alterar.</p>
        )}
      </div>
      {podeEditar && (
        <div className="set-section__foot">
          <AsyncButton
            onClick={salvar}
            disabled={!mudou || Boolean(erroCnpj)}
            successMessage="Dados da empresa salvos."
            silentError
          >
            Salvar
          </AsyncButton>
        </div>
      )}
    </section>
  );
}

function FormLocal({ inicial, onCancelar, onSalvar }) {
  const [form, setForm] = useState(inicial);
  const [erro, setErro] = useState("");
  const alterar = (campo, valor) => setForm((f) => ({ ...f, [campo]: valor }));
  const sair = (campo, valor) => setForm((f) => ({ ...f, [campo]: normalizarAoSair(campo, valor) }));

  async function salvar() {
    setErro("");
    try {
      await onSalvar(form);
    } catch (e) {
      setErro(e?.message || "Não foi possível salvar o local.");
      throw e;
    }
  }

  return (
    <div className="me-local-form">
      <div className="set-fields">
        <Campo prefixo="local" def={{ campo: "nome", rotulo: "Nome do local" }} valor={form.nome} onChange={alterar} onBlur={sair} />
        <div className="input-group">
          <label className="input-label" htmlFor="local-tipo">Tipo</label>
          <select id="local-tipo" className="input" value={form.tipo} onChange={(e) => alterar("tipo", e.target.value)}>
            {TIPOS_LOCAL.map((t) => <option key={t.valor} value={t.valor}>{t.rotulo}</option>)}
          </select>
        </div>
        {CAMPOS_ENDERECO.map((def) => (
          <Campo key={def.campo} prefixo="local" def={def} valor={form[def.campo]} onChange={alterar} onBlur={sair} />
        ))}
      </div>
      <label className="me-check">
        <input
          type="checkbox"
          checked={form.saida_das_entregas}
          onChange={(e) => alterar("saida_das_entregas", e.target.checked)}
        />
        O caminhão das entregas sai daqui
      </label>
      {erro && <p className="me-erro" role="alert">{erro}</p>}
      <div className="me-acoes">
        <AsyncButton onClick={salvar} disabled={!form.nome.trim()} successMessage="Local salvo." silentError>
          Salvar local
        </AsyncButton>
        <button type="button" className="btn btn--ghost" onClick={onCancelar}>Cancelar</button>
      </div>
    </div>
  );
}

function Locais({ podeEditar }) {
  const { request } = useAuth();
  const [locais, setLocais] = useState(null);
  const [erro, setErro] = useState("");
  const [editando, setEditando] = useState(null);

  const carregar = useCallback(async () => {
    try {
      const dados = await listarLocais(request);
      setLocais(dados.locais || []);
      setErro("");
    } catch (e) {
      setErro(e?.message || "Não foi possível carregar os locais.");
    }
  }, [request]);

  useEffect(() => { carregar(); }, [carregar]);

  async function salvar(form) {
    const dados = { ...form, cep: String(form.cep || "").replace(/\D/g, "") };
    if (editando === "novo") await criarLocal(request, dados);
    else await salvarLocal(request, editando.id, dados);
    setEditando(null);
    await carregar();
  }

  async function alternarAtivo(local) {
    await salvarLocal(request, local.id, { ativo: !local.ativo });
    await carregar();
  }

  return (
    <section className="set-section">
      <div className="set-section__head">
        <h2 className="set-section__title">Locais</h2>
        <p className="set-section__desc">
          Fábrica, showroom e depósitos da empresa. Todos veem os mesmos dados; o local
          marcado como saída das entregas vira o ponto de partida ao otimizar a rota de uma carga.
        </p>
      </div>
      <div className="set-section__body">
        {erro && <p className="me-erro" role="alert">{erro}</p>}
        {locais === null && !erro && <p className="me-dica">Carregando locais…</p>}
        {locais && locais.length === 0 && editando !== "novo" && (
          <p className="me-dica">Nenhum local cadastrado ainda.</p>
        )}
        {locais && locais.length > 0 && (
          <ul className="me-locais">
            {locais.map((local) => (
              <li key={local.id} className={local.ativo ? "" : "inativo"}>
                {editando?.id === local.id ? (
                  <FormLocal inicial={{ ...local, cep: formatarCep(local.cep) }} onCancelar={() => setEditando(null)} onSalvar={salvar} />
                ) : (
                  <>
                    <div className="me-local-texto">
                      <strong>{local.nome}</strong>
                      <span>
                        {local.tipo_rotulo}
                        {local.saida_das_entregas && " · saída das entregas"}
                        {!local.ativo && " · inativo"}
                      </span>
                      {local.endereco_completo && <span className="me-local-end" title={local.endereco_completo}>{local.endereco_completo}</span>}
                    </div>
                    {podeEditar && (
                      <div className="me-acoes">
                        <button type="button" className="btn btn--ghost" onClick={() => setEditando(local)}>Editar</button>
                        <AsyncButton
                          variant="secondary"
                          onClick={() => alternarAtivo(local)}
                          successMessage={local.ativo ? "Local desativado." : "Local reativado."}
                        >
                          {local.ativo ? "Desativar" : "Reativar"}
                        </AsyncButton>
                      </div>
                    )}
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
        {editando === "novo" && (
          <FormLocal inicial={LOCAL_VAZIO} onCancelar={() => setEditando(null)} onSalvar={salvar} />
        )}
        {podeEditar && editando === null && (
          <button type="button" className="btn btn--ghost me-adicionar" onClick={() => setEditando("novo")}>
            + Adicionar local
          </button>
        )}
      </div>
    </section>
  );
}

function SituacaoDasConfiguracoes({ configuracoes }) {
  const { hasAnyPermission } = useAuth();
  return (
    <section className="set-section">
      <div className="set-section__head">
        <h2 className="set-section__title">Configurações da empresa</h2>
        <p className="set-section__desc">O que já está pronto e o que falta configurar para operar.</p>
      </div>
      <div className="set-section__body">
        <ul className="me-config">
          {configuracoes.map((c) => (
            <li key={c.chave}>
              <span className={`me-status ${c.configurado ? "ok" : "falta"}`}>
                {c.configurado ? "Configurado" : "Falta configurar"}
              </span>
              <span className="me-config-rotulo">{c.rotulo}</span>
              {(!c.permissao || hasAnyPermission([c.permissao])) && (
                <Link className="btn btn--ghost" to={c.link}>Abrir</Link>
              )}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export default function MinhaEmpresa() {
  const { request } = useAuth();
  const [empresa, setEmpresa] = useState(null);
  const [erro, setErro] = useState("");

  const carregar = useCallback(async () => {
    try {
      setEmpresa(await lerMinhaEmpresa(request));
      setErro("");
    } catch (e) {
      setErro(e?.message || "Não foi possível carregar os dados da empresa.");
    }
  }, [request]);

  useEffect(() => { carregar(); }, [carregar]);

  if (erro) {
    return (
      <section className="set-section">
        <div className="set-section__body">
          <p className="me-erro" role="alert">{erro}</p>
          <button type="button" className="btn btn--ghost" onClick={carregar}>Tentar de novo</button>
        </div>
      </section>
    );
  }
  if (!empresa) {
    return (
      <section className="set-section">
        <div className="set-section__body"><p className="me-dica">Carregando…</p></div>
      </section>
    );
  }
  return (
    <>
      <DadosDaEmpresa dados={empresa.dados} podeEditar={empresa.pode_editar} onSalvo={setEmpresa} />
      <LogoDaEmpresa />
      <Locais podeEditar={empresa.pode_editar} />
      <SituacaoDasConfiguracoes configuracoes={empresa.configuracoes || []} />
    </>
  );
}
